<script setup lang="ts">
/**
 * 设计树 Carbon 图标组件。
 *
 * 职责：
 * 1. 将设计树节点、状态和右键菜单动作统一映射到 `@vicons/carbon` 图标；
 * 2. 通过 Naive UI 的 `NIcon` 统一控制尺寸与颜色，避免图标渲染逻辑散落在设计树组件中；
 * 3. 保留字符串类型入口，兼容设计树投影层现有的 `iconType` 扩展方式。
 */
import { computed, type Component } from 'vue';
import { NIcon } from 'naive-ui';
import {
  Add,
  ArrowRight,
  ModelAlt,
  Checkmark,
  ColorPalette,
  Copy,
  Cube,
  Development,
  Document,
  Edit,
  Error,
  Folder,
  Layers,
  Link,
  Locked,
  DotMark,
  DeliveryParcel,
  TextAnnotationToggle,
  Notebook,
  Pin,
  Blockchain,
  RadioButton,
  RecentlyViewed,
  Share,
  SubtractAlt,
  TrashCan,
  Tree,
  Subtract,
  View,
  ViewOff,
  Warning,
  Area,
  BrushPolygon
} from '@vicons/carbon';

/**
 * 设计树图标渲染配置。
 */
interface FeatureTreeIconConfig {
  /** Carbon 图标组件。 */
  component: Component;
  /** 图标颜色，统一由 `NIcon` 的 color 属性消费。 */
  color: string;
}

/**
 * 设计树图标配置映射表。
 */
type FeatureTreeIconConfigMap = Record<string, FeatureTreeIconConfig>;

const DEFAULT_FEATURE_TREE_ICON_CONFIG: FeatureTreeIconConfig = {
  component: Document,
  color: 'var(--puppy-primary)',
};

const FEATURE_TREE_ICON_CONFIG_BY_TYPE: FeatureTreeIconConfigMap = {
  'part-root': { component: Tree, color: 'var(--puppy-primary)' },
  assembly: { component: Development, color: '#959595' },
  part: { component: ModelAlt, color: '#959595' },
  'datum-plane': { component: Blockchain, color: 'var(--puppy-primary)' },
  'datum-plane-placeholder': { component: Blockchain, color: 'var(--puppy-primary)' },
  'datum-axis': { component: ArrowRight, color: '#0d9488' },
  'datum-point': { component: RadioButton, color: '#dc2626' },
  axis: { component: ArrowRight, color: '#0d9488' },
  line: { component: ArrowRight, color: '#0d9488' },
  point: { component: RadioButton, color: '#dc2626' },
  origin: { component: DotMark, color: '#dc2626' },
  sketch: { component: Area, color: 'var(--puppy-primary)' },
  feature: { component: DeliveryParcel, color: '#959595' },
  body: { component: Cube, color: 'var(--puppy-primary)' },
  history: { component: RecentlyViewed, color: '#64748b' },
  material: { component: ColorPalette, color: '#dc2626' },
  annotation: { component: Notebook, color: 'var(--puppy-primary)' },
  group: { component: Folder, color: '#959595' },
  'body-folder': { component: Folder, color: '#959595' },
  'extrude-feature': { component: DeliveryParcel, color: '#959595' },
  'revolve-feature': { component: DeliveryParcel, color: '#959595' },
  'rollback-bar': { component: SubtractAlt, color: '#b7791f' },
  'glb-node': { component: Layers, color: 'var(--puppy-primary)' },
  'glb-datum-slot': { component: Blockchain, color: 'var(--puppy-primary)' },
  'assembly-mate-folder': { component: Folder, color: '#64748b' },
  'assembly-mate': { component: Link, color: 'var(--puppy-primary)' },
  'assembly-mate-lock-rotation': { component: Locked, color: 'var(--puppy-primary)' },
  'action-create-datum-plane': { component: Add, color: 'var(--puppy-primary)' },
  'action-enter-sketch': { component: BrushPolygon, color: 'var(--puppy-primary)' },
  'action-edit-sketch': { component: BrushPolygon, color: 'var(--puppy-primary)' },
  'action-edit-feature': { component: TextAnnotationToggle, color: 'var(--puppy-primary)' },
  'action-copy-sketch': { component: Copy, color: '#959595' },
  'action-rename': { component: Edit, color: '#959595' },
  'action-delete': { component: TrashCan, color: '#dc2626' },
  'action-hide': { component: ViewOff, color: '#64748b' },
  'action-show': { component: View, color: '#959595' },
  'action-fix': { component: Pin, color: 'var(--puppy-primary)' },
  'action-float': { component: Subtract, color: 'var(--puppy-primary)' },
  'action-tree-display': { component: Tree, color: 'var(--puppy-primary)' },
  'action-rollback': { component: SubtractAlt, color: '#b7791f' },
  'action-roll-forward': { component: ArrowRight, color: '#b7791f' },
  'action-roll-previous': { component: RecentlyViewed, color: '#b7791f' },
  'action-roll-end': { component: Checkmark, color: '#b7791f' },
  'status-hidden': { component: ViewOff, color: '#959595' },
  'status-suppressed': { component: SubtractAlt, color: '#f59e0b' },
  'status-error': { component: Error, color: '#dc2626' },
  'status-shared': { component: Share, color: '#16a34a' },
  'status-reference': { component: Link, color: 'var(--puppy-primary)' },
  'status-checked': { component: Checkmark, color: '#16a34a' },
  'status-warning': { component: Warning, color: '#f59e0b' },
  fallback: { component: Blockchain, color: 'var(--puppy-primary)' },
};

const props = withDefaults(defineProps<{
  /** 设计树投影或菜单传入的图标类型。 */
  iconType: string;
  /** 图标尺寸，默认跟随设计树行高使用 14px。 */
  size?: number;
  /** 外部覆盖颜色，菜单或特殊状态需要临时改色时使用。 */
  color?: string;
}>(), {
  size: 18,
  color: '',
});

const resolvedIconConfig = computed<FeatureTreeIconConfig>(() => {
  /**
   * 设计树投影允许新增 `iconType`，未知类型必须退回稳定的默认图标，
   * 避免新增节点类型时因为 UI 映射未更新导致整行渲染失败。
   */
  return FEATURE_TREE_ICON_CONFIG_BY_TYPE[props.iconType]
    ?? FEATURE_TREE_ICON_CONFIG_BY_TYPE.fallback
    ?? DEFAULT_FEATURE_TREE_ICON_CONFIG;
});

const resolvedColor = computed(() => {
  /**
   * 外部传入颜色时优先生效，保证右键菜单等局部场景可以复用同一套图标组件。
   */
  return props.color || resolvedIconConfig.value.color;
});
</script>

<template>
  <n-icon
    class="feature-tree-node-icon"
    :size="size"
    :color="resolvedColor"
    :depth="1"
    aria-hidden="true"
  >
    <component :is="resolvedIconConfig.component" />
  </n-icon>
</template>

<style scoped>
.feature-tree-node-icon {
  flex-shrink: 0;
}
</style>
